// src/components/MemberRow.js
import React, {useState} from "react";
import axios from "axios";
import {menuApi} from "../api/menu";
import {
    PencilIcon,
    CheckIcon,
    XMarkIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";

/**
 * Props:
 *   member   – объект Member (id, first_name, last_name, patronymic, gender, email, token, last_visit, last_rsvp_change)
 *   familyId – id семьи, к которой привязан член
 *   linkId   – id записи FamilyMember (нужен для PATCH/DELETE ссылки)
 *   attending   – текущий статус RSVP (true/false/undefined)
 *   canVote     – права «отвечает за всех» (true/false)
 *   reload      – функция, вызываемая после любого изменения,
 *                обычно loadData(auth) из Admin.js
 */
export default function MemberRow({
                                      member,
                                      familyId,
                                      linkId,
                                      attending,
                                      canVote,
                                      menuTag,
                                      menuItems,
                                      reload,
                                      copyLink
                                  }) {
    // -----------------------------------------------------------------
    // 1️⃣ UI‑состояния
    // -----------------------------------------------------------------
    const [editMode, setEditMode] = useState(false);
    const [draft, setDraft] = useState({...member});
    const [rsvp, setRsvp] = useState(member.is_attending);
    const [vote, setVote] = useState(!!canVote);
    const [confirmDel, setConfirmDel] = useState(null); // null | "unlink" | "full"
    const [menuEdit, setMenuEdit] = useState(false);
    const [menuSaving, setMenuSaving] = useState(false);
    const [menuDraft, setMenuDraft] = useState({
        appetizer_id: menuTag?.appetizer_id ?? "",
        main_dish_id: menuTag?.main_dish_id ?? "",
        drink_id: menuTag?.drink_id ?? "",
    });

    // -----------------------------------------------------------------
    // 2️⃣ Сохранить изменения
    // -----------------------------------------------------------------
    const saveChanges = async () => {
        try {
            // 2.1 Обновляем данные участника
            await axios.patch(`/api/admin/members/${member.id}`, draft);

            // 2.2 Обновляем связь «семья‑участник» (RSVP + право‑голосования)
            await axios.patch(
                `/api/admin/families/${familyId}/members/${linkId}`,
                {can_vote_for_others: vote}
            );

            setEditMode(false);
            reload();
        } catch (e) {
            alert(e.response?.data?.detail || "Не удалось сохранить изменения");
        }
    };

    // -----------------------------------------------------------------
    // 3️⃣ Отменить правку
    // -----------------------------------------------------------------
    const cancelChanges = () => {
        setDraft({...member});
        setRsvp(attending);
        setVote(!!canVote);
        setEditMode(false);
    };

    // -----------------------------------------------------------------
    // 3️⃣.5 Меню: сохранить / сбросить / отменить
    // -----------------------------------------------------------------
    const itemsByType = {
        appetizer: (menuItems || []).filter((i) => i.item_type === "appetizer"),
        main_dish: (menuItems || []).filter((i) => i.item_type === "main_dish"),
        drink: (menuItems || []).filter((i) => i.item_type === "drink"),
    };

    const beginMenuEdit = () => {
        setMenuDraft({
            appetizer_id: menuTag?.appetizer_id ?? "",
            main_dish_id: menuTag?.main_dish_id ?? "",
            drink_id: menuTag?.drink_id ?? "",
        });
        setMenuEdit(true);
    };

    const saveMenu = async () => {
        setMenuSaving(true);
        try {
            const payload = {
                appetizer_id: menuDraft.appetizer_id === "" ? null : Number(menuDraft.appetizer_id),
                main_dish_id: menuDraft.main_dish_id === "" ? null : Number(menuDraft.main_dish_id),
                drink_id: menuDraft.drink_id === "" ? null : Number(menuDraft.drink_id),
            };
            await menuApi.saveMemberSelection(member.id, payload);
            setMenuEdit(false);
            reload();
        } catch (e) {
            alert(e.response?.data?.detail || "Не удалось сохранить меню");
        } finally {
            setMenuSaving(false);
        }
    };

    const clearMenu = async () => {
        if (!window.confirm("Сбросить выбор меню для этого гостя?")) return;
        setMenuSaving(true);
        try {
            await menuApi.clearMemberSelection(member.id);
            setMenuEdit(false);
            reload();
        } catch (e) {
            alert(e.response?.data?.detail || "Не удалось сбросить меню");
        } finally {
            setMenuSaving(false);
        }
    };

    // -----------------------------------------------------------------
    // 4️⃣ Удалить (полностью или только из семьи)
    // -----------------------------------------------------------------
    const removeMember = async (full) => {
        try {
            if (full) {
                await axios.delete(`/api/admin/members/${member.id}`);
            } else {
                await axios.delete(
                    `/api/admin/families/${familyId}/members/${linkId}`
                );
            }
            setConfirmDel(null);
            reload();
        } catch (e) {
            alert(e.response?.data?.detail || "Не удалось удалить");
        }
    };

    // -----------------------------------------------------------------
    // 5️⃣ Вспомогательные: статус RSVP и форматирование дат
    // -----------------------------------------------------------------
    const getRsvpStatus = () => {
        if (member.is_attending === true) return {text: "Придёт", class: "bg-green-100 text-green-800"};
        if (member.is_attending === false) return {text: "Не придёт", class: "bg-red-100 text-red-800"};
        return {text: "Не отвечал", class: "bg-gray-100 text-gray-600"};
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "Никогда";
        const date = new Date(dateStr);  // dateStr из backend — в UTC, но мы отображаем в MSK
        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: 'Europe/Moscow',
            hour12: false,
        };
        return new Intl.DateTimeFormat('ru-RU', options).format(date);
    };

    // -----------------------------------------------------------------
    // 6️⃣ JSX
    // -----------------------------------------------------------------
    return (
        <li className="flex items-center justify-between p-2 bg-gray-50 rounded">
            {/* ── Информация о госте ── */}
            <div className="flex-1 mr-4">
                {editMode ? (
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            className="p-1 border rounded"
                            value={draft.last_name}
                            placeholder="Фамилия"
                            onChange={(e) =>
                                setDraft({...draft, last_name: e.target.value})
                            }
                        />
                        <input
                            className="p-1 border rounded"
                            value={draft.first_name}
                            placeholder="Имя"
                            onChange={(e) =>
                                setDraft({...draft, first_name: e.target.value})
                            }
                        />
                        <input
                            className="p-1 border rounded col-span-2"
                            value={draft.patronymic ?? ""}
                            placeholder="Отчество"
                            onChange={(e) =>
                                setDraft({...draft, patronymic: e.target.value})
                            }
                        />
                        <select
                            className="p-1 border rounded"
                            value={draft.gender}
                            onChange={(e) =>
                                setDraft({...draft, gender: e.target.value})
                            }
                        >
                            <option value="male">Мужской</option>
                            <option value="female">Женский</option>
                        </select>
                        <input
                            className="p-1 border rounded col-span-2"
                            value={draft.email ?? ""}
                            placeholder="E‑mail"
                            onChange={(e) =>
                                setDraft({...draft, email: e.target.value})
                            }
                        />
                    </div>
                ) : (
                    <div>
                        {/* Имя + пол */}
                        <span>
              {member.last_name} {member.first_name}{" "}
                            {member.patronymic || ""} ({member.gender})
            </span>

                        {/* Новое: Статус RSVP */}
                        <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getRsvpStatus().class}`}>
              {getRsvpStatus().text}
            </span>

                        {/* Новое: Тег "отвечает за всех" (если есть) */}
                        {canVote && (
                            <span className="ml-2 text-xs bg-gold-accent text-white px-2 py-1 rounded">
                отвечает за всех
              </span>
                        )}
                        {member.is_second_wave && (
                            <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-1 rounded">Донабор</span>
                        )}

                        {/* Новое: Выбранное меню (теги блюд) — либо режим правки */}
                        {menuEdit ? (
                            <div className="mt-1 space-y-1 text-xs">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-600 w-14">Закуска:</span>
                                    <select
                                        className="p-1 border rounded text-sm"
                                        value={menuDraft.appetizer_id}
                                        onChange={(e) => setMenuDraft({...menuDraft, appetizer_id: e.target.value})}
                                    >
                                        <option value="">— не выбрано —</option>
                                        {itemsByType.appetizer.map((i) => (
                                            <option key={i.id} value={i.id}>{i.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-600 w-14">Основное:</span>
                                    <select
                                        className="p-1 border rounded text-sm"
                                        value={menuDraft.main_dish_id}
                                        onChange={(e) => setMenuDraft({...menuDraft, main_dish_id: e.target.value})}
                                    >
                                        <option value="">— не выбрано —</option>
                                        {itemsByType.main_dish.map((i) => (
                                            <option key={i.id} value={i.id}>{i.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-600 w-14">Напиток:</span>
                                    <select
                                        className="p-1 border rounded text-sm"
                                        value={menuDraft.drink_id}
                                        onChange={(e) => setMenuDraft({...menuDraft, drink_id: e.target.value})}
                                    >
                                        <option value="">— не выбрано —</option>
                                        {itemsByType.drink.map((i) => (
                                            <option key={i.id} value={i.id}>{i.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                    <button
                                        onClick={saveMenu}
                                        disabled={menuSaving}
                                        className="bg-green-600 text-white text-xs px-3 py-1 rounded disabled:opacity-50"
                                    >
                                        {menuSaving ? "Сохранение..." : "Сохранить меню"}
                                    </button>
                                    {menuTag && (menuTag.appetizer || menuTag.main_dish || menuTag.drink) && (
                                        <button
                                            onClick={clearMenu}
                                            disabled={menuSaving}
                                            className="bg-red-600 text-white text-xs px-3 py-1 rounded disabled:opacity-50"
                                        >
                                            Сбросить
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setMenuEdit(false)}
                                        className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded"
                                    >
                                        Отмена
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-1 text-xs space-y-0.5">
                                {(menuTag && (menuTag.appetizer || menuTag.main_dish || menuTag.drink)) ? (
                                    <>
                                        {menuTag.appetizer && (
                                            <div><span className="font-medium text-gray-600">Закуска:</span> <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">{menuTag.appetizer}</span></div>
                                        )}
                                        {menuTag.main_dish && (
                                            <div><span className="font-medium text-gray-600">Основное:</span> <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">{menuTag.main_dish}</span></div>
                                        )}
                                        {menuTag.drink && (
                                            <div><span className="font-medium text-gray-600">Напиток:</span> <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">{menuTag.drink}</span></div>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-gray-400">Меню не выбрано</span>
                                )}
                                {(menuItems && menuItems.length > 0) && (
                                    <button
                                        onClick={beginMenuEdit}
                                        className="ml-1 text-blue-600 hover:underline"
                                        title="Назначить/изменить меню"
                                    >
                                        {menuTag ? "изменить" : "назначить"} меню
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Новое: Даты (в одной строке, компактно) */}
                        <div className="mt-1 text-xs text-gray-500 space-x-2">
                            <span>Визит: {formatDate(member.last_visit)}</span>
                            <span>RSVP: {formatDate(member.last_rsvp_change)}</span>
                        </div>
                    </div>
                )}

                {/* RSVP‑чекбоксы – показываем только в режиме правки */}
                {editMode && (
                    <div className="mt-2 flex items-center gap-4">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={rsvp === true}
                                onChange={(e) => setRsvp(e.target.checked)}
                                className="mr-1"
                            />
                            Придёт
                        </label>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={vote}
                                onChange={(e) => setVote(e.target.checked)}
                                className="mr-1"
                            />
                            «Отвечает за всех»
                        </label>


                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={draft.is_second_wave || false}
                                onChange={(e) => setDraft({...draft, is_second_wave: e.target.checked})}
                                className="mr-1"
                            />
                            Донабор (вторая очередь)
                        </label>
                        {/* ... остальные чекбоксы */}

                    </div>
                )}
            </div>

            {/* ── Кнопки управления ── */}
            <div className="flex items-center gap-2">
                {editMode ? (
                    <>
                        <button
                            onClick={saveChanges}
                            title="Сохранить"
                            className="text-green-600"
                        >
                            <CheckIcon className="h-5 w-5"/>
                        </button>
                        <button
                            onClick={cancelChanges}
                            title="Отмена"
                            className="text-gray-600"
                        >
                            <XMarkIcon className="h-5 w-5"/>
                        </button>
                    </>
                ) : (
                    <button
                        onClick={() => setEditMode(true)}
                        title="Редактировать"
                        className="text-blue-600"
                    >
                        <PencilIcon className="h-5 w-5"/>
                    </button>
                )}

                {/* Удалить из семьи (оставить гостя) */}
                <button
                    onClick={() => setConfirmDel("unlink")}
                    title="Удалить из семьи"
                    className="text-yellow-600"
                >
                    <TrashIcon className="h-5 w-5"/>
                </button>

                {/* Полное удаление гостя */}
                <button
                    onClick={() => setConfirmDel("full")}
                    title="Полностью удалить"
                    className="text-red-600"
                >
                    <TrashIcon className="h-5 w-5"/>
                </button>

                {/* Кнопка «Скопировать ссылку» */}
                <button
                    onClick={() => copyLink(member.token)}          // ← вызываем функцию из Admin
                    title="Скопировать ссылку"
                    className="text-sm bg-gray-200 px-3 py-1 rounded"
                >
                    Скопировать ссылку
                </button>

            </div>

            {/* ── Модальное окно подтверждения ── */}
            {confirmDel && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white p-4 rounded shadow-md">
                        <p className="mb-3">
                            {confirmDel === "full"
                                ? "Вы действительно хотите полностью удалить этого гостя?"
                                : "Удалить гостя только из этой семьи (сохранить запись в базе)?"}
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                className="px-3 py-1 bg-gray-200 rounded"
                                onClick={() => setConfirmDel(null)}
                            >
                                Отмена
                            </button>
                            <button
                                className="px-3 py-1 bg-red-600 text-white rounded"
                                onClick={() => removeMember(confirmDel === "full")}
                            >
                                Да, удалить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </li>
    );
}